'use client';

import * as React from 'react';
import { Icon } from '~/components/primitives';
import { DayPicker, getDefaultClassNames } from 'react-day-picker';
import { cn } from '~/utils';
import { Button, buttonVariants } from '~/components/ui/button';

export type CalendarProps = React.ComponentProps<typeof DayPicker> & {
    buttonVariant?: React.ComponentProps<typeof Button>['variant'];
};

function Calendar({
    className,
    classNames,
    showOutsideDays = true,
    captionLayout = 'label',
    buttonVariant = 'ghost',
    formatters,
    components,
    ...props
}: CalendarProps) {
    const defaultClassNames = getDefaultClassNames();

    return (
        <DayPicker
            weekStartsOn={1}
            showOutsideDays={showOutsideDays}
            className={cn('p-3', className)}
            captionLayout={captionLayout}
            formatters={{
                formatMonthDropdown: date => date.toLocaleString('default', { month: 'short' }),
                ...formatters,
            }}
            classNames={{
                root: cn('w-fit', defaultClassNames.root),
                months: cn(
                    'flex flex-col sm:flex-row space-y-4 sm:space-y-0 relative gap-2',
                    defaultClassNames.months,
                ),
                month: cn('space-y-4', defaultClassNames.month),
                month_caption: cn(
                    'flex justify-center pt-1 relative items-center',
                    defaultClassNames.month_caption,
                ),
                month_grid: 'w-full border-collapse space-y-1',
                nav: cn(
                    'flex items-center justify-between absolute inset-x-0 top-0 z-20 pointer-events-none pt-1',
                    defaultClassNames.nav,
                ),
                button_previous: cn(
                    buttonVariants({ variant: buttonVariant }),
                    'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 pointer-events-auto',
                    defaultClassNames.button_previous,
                ),
                button_next: cn(
                    buttonVariants({ variant: buttonVariant }),
                    'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 pointer-events-auto',
                    defaultClassNames.button_next,
                ),

                // Dropdown styles for captionLayout="dropdown"
                dropdowns: cn(
                    'flex items-center gap-2 justify-center relative',
                    defaultClassNames.dropdowns,
                ),
                dropdown_root: cn(
                    'relative border border-input rounded-md has-focus:border-ring has-focus:ring-ring/50 has-focus:ring-[3px]',
                    defaultClassNames.dropdown_root,
                ),
                dropdown: cn(
                    'absolute inset-0 opacity-0 cursor-pointer',
                    defaultClassNames.dropdown,
                ),
                caption_label: cn(
                    'text-sm font-medium select-none h-8 flex items-center',
                    captionLayout === 'dropdown' &&
                        'rounded-md pl-2 pr-1 gap-1 [&>svg]:text-muted-foreground [&>svg]:size-3.5',
                    defaultClassNames.caption_label,
                ),

                weeks: 'w-full border-collapse',
                weekdays: cn('flex gap-0.5', defaultClassNames.weekdays),
                weekday: cn(
                    'text-muted-foreground rounded-md w-8 font-normal text-[0.8rem]',
                    defaultClassNames.weekday,
                ),
                week: cn('flex w-full gap-0.5 mt-2', defaultClassNames.week),
                day: cn(
                    'h-8 w-8 flex items-center justify-center rounded-md font-normal aria-selected:opacity-100 hover:text-white hover:bg-primary [&>button]:w-full [&>button]:h-full [&>button]:rounded-md',
                    defaultClassNames.day,
                ),
                range_start: cn(
                    'day-range-start !bg-accent rounded-l-md [&>button]:bg-primary [&>button]:text-white [&>button]:hover:bg-primary [&>button]:hover:text-white [&>button]:rounded-md',
                    defaultClassNames.range_start,
                ),
                range_end: cn(
                    'day-range-end !bg-accent rounded-r-md [&>button]:bg-primary [&>button]:text-white [&>button]:hover:bg-primary [&>button]:hover:text-white [&>button]:rounded-md',
                    defaultClassNames.range_end,
                ),
                range_middle: cn(
                    'aria-selected:bg-accent aria-selected:text-accent-foreground [&>button]:rounded-none',
                    defaultClassNames.range_middle,
                ),
                selected: cn(
                    props.mode === 'range'
                        ? 'bg-primary text-white hover:bg-primary hover:text-white focus:bg-primary focus:text-white'
                        : 'bg-primary text-white [&>button]:bg-primary [&>button]:text-white [&>button]:focus:bg-primary [&>button]:focus:text-white',
                    defaultClassNames.selected,
                ),
                today: cn('bg-secondary text-white [&>button]:rounded-md', defaultClassNames.today),
                outside: cn('text-muted-foreground opacity-50', defaultClassNames.outside),
                disabled: cn('text-muted-foreground opacity-50', defaultClassNames.disabled),
                hidden: cn('invisible', defaultClassNames.hidden),
                ...classNames,
            }}
            components={{
                Chevron: ({
                    className,
                    orientation,
                    size: _size,
                    disabled: _disabled,
                    ...props
                }) => {
                    if (orientation === 'left') {
                        return (
                            <Icon
                                name="arrow-left"
                                className={cn('h-4 w-4', className)}
                                {...props}
                            />
                        );
                    }
                    if (orientation === 'right') {
                        return (
                            <Icon
                                name="arrow-right"
                                className={cn('h-4 w-4', className)}
                                {...props}
                            />
                        );
                    }
                    return (
                        <Icon name="chevron-down" className={cn('h-4 w-4', className)} {...props} />
                    );
                },
                ...components,
            }}
            {...props}
        />
    );
}

Calendar.displayName = 'Calendar';

export { Calendar };
